import { prefersReducedMotion } from "@theme/utilities";

/**
 * Reveals product-grid tiles as they scroll into view — but ONLY tiles that
 * start below the fold. Tiles already on screen at load stay static (no reveal
 * animation on page load). Tiles the paginator hides (display:none → zero-size)
 * are also treated as below-fold, so paginated rows animate when they're shown
 * and scrolled to.
 *
 * Tiles render visible by default (see the reveal rules in app.css), so no-JS,
 * reduced-motion, and a failed upgrade all show the full grid. This element is
 * behaviour-only and renders nothing itself.
 */
class GridReveal extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  _observer = null;

  connectedCallback() {
    if (prefersReducedMotion()) return;
    // Defer one frame so the tiles are laid out before we measure the fold line.
    requestAnimationFrame(() => this._init());
  }

  disconnectedCallback() {
    this._observer?.disconnect();
  }

  _init() {
    const root = this.closest("tile-paginator") || document;
    const tiles = root.querySelectorAll(".product-grid__item");
    if (!tiles.length) return;

    this._observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          entry.target.classList.replace("reveal-pending", "reveal-in");
          this._observer?.unobserve(entry.target);
        }
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.1 },
    );

    const foldline = window.innerHeight;
    for (const tile of tiles) {
      const rect = tile.getBoundingClientRect();
      const hidden = rect.width === 0 && rect.height === 0; // paginator-hidden
      if (rect.top >= foldline || hidden) {
        tile.classList.add("reveal-pending");
        this._observer.observe(tile);
      }
    }
  }
}

if (!customElements.get("grid-reveal")) {
  customElements.define("grid-reveal", GridReveal);
}
